'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { InstructorDetails } from '@udemy-v2/browse-course';
import styles from './instructor-display.module.scss';
import classNames from 'classnames';
import { useI18n, LocalizedHtml } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { generateInstructorAnchorHash } from '../instructor-module/utils/instructor-utils';
import { Icon } from '@udemy-v2/icons';
import { useInstructorCommunityBannerInfo } from '@/lib/hooks/use-instructor-community-banner-info';
import { useInstructorDetails } from '@/lib/hooks/use-instructor-details';
import { CommunityPoweredBadge } from '../instructor-community/community-powered-badge';
import { CommunityPoweredRibbon } from '../instructor-community/community-powered-ribbon';
interface InstructorDisplayProps {
  instructors: InstructorDetails[];
  isAGwGCourse?: boolean;
}

/**
 * Displays course instructors with links to their profiles
 *
 * @example
 * ```jsx
 * <InstructorDisplay instructors={course.instructors} />
 * ```
 */
export function InstructorDisplay(t0) {
  const $ = _c(26);
  const {
    instructors,
    isAGwGCourse: t1
  } = t0;
  const isAGwGCourse = t1 === undefined ? false : t1;
  const {
    gettext,
    interpolate
  } = useI18n();
  const firstInstructor = instructors?.[0];
  const {
    hasCommunity
  } = useInstructorCommunityBannerInfo(firstInstructor?.id);
  const instructorDetails = useInstructorDetails();
  if (!instructors?.length) {
    return null;
  }
  const showCommunityPoweredBadge = !isAGwGCourse && hasCommunity;
  let t2;
  if ($[0] !== firstInstructor?.id || $[1] !== instructorDetails) {
    let t3;
    if ($[3] !== firstInstructor?.id) {
      t3 = instructor => instructor.id === firstInstructor?.id;
      $[3] = firstInstructor?.id;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = instructorDetails.find(t3);
    $[0] = firstInstructor?.id;
    $[1] = instructorDetails;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const firstInstructorDetails = t2;
  const firstInstructorImageUrl = firstInstructorDetails?.images.large ?? firstInstructor?.images.small;
  const handleOnInstructorNameClick = _temp;
  let t3;
  if ($[5] !== firstInstructor || $[6] !== firstInstructorImageUrl || $[7] !== showCommunityPoweredBadge) {
    t3 = () => {
      if (!showCommunityPoweredBadge || !firstInstructorImageUrl) {
        return null;
      }
      return <div className={styles["instructor-header-leading"]} data-purpose="instructor-header-image"><div className={styles["instructor-header-image-container"]}><CommunityPoweredBadge imageUrl={firstInstructorImageUrl} displayName={firstInstructor.name} url={firstInstructor.url} size="sm" /></div></div>;
    };
    $[5] = firstInstructor;
    $[6] = firstInstructorImageUrl;
    $[7] = showCommunityPoweredBadge;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderInstructorHeaderImage = t3;
  let t4;
  if ($[9] !== gettext || $[10] !== instructors || $[11] !== interpolate || $[12] !== isAGwGCourse) {
    t4 = () => {
      const Names = () => {
        if (isAGwGCourse) {
          return <Button componentClass="a" udStyle="link" className={classNames("ud-text-sm", styles["instructor-link"])} onClick={handleOnInstructorNameClick} href={generateInstructorAnchorHash(0)}>Google</Button>;
        }
        const instructorButtons = instructors.map(_temp2);
        return instructorButtons.reduce(_temp3, []);
      };
      return <span className={styles["instructor-container"]}>{isAGwGCourse && <Icon glyph="google" size="xsmall" label={false} className={styles["google-icon"]} />}<LocalizedHtml html={interpolate(gettext("<span class=\"created-by\">Created by</span>"), true)} interpolate={{
          "created-by": <span className={styles["created-by"]} />
        }} /><Names /></span>;
    };
    $[9] = gettext;
    $[10] = instructors;
    $[11] = interpolate;
    $[12] = isAGwGCourse;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const renderInstructorNames = t4;
  if (!showCommunityPoweredBadge) {
    let t5;
    if ($[14] !== renderInstructorNames) {
      t5 = renderInstructorNames();
      $[14] = renderInstructorNames;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  let t5;
  if ($[16] !== renderInstructorHeaderImage) {
    t5 = renderInstructorHeaderImage();
    $[16] = renderInstructorHeaderImage;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== renderInstructorNames) {
    t6 = renderInstructorNames();
    $[18] = renderInstructorNames;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CommunityPoweredRibbon />;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6) {
    t8 = <div className={styles["instructor-header-details"]}>{t6}{t7}</div>;
    $[21] = t6;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t5 || $[24] !== t8) {
    t9 = <div className={styles["with-community-badge"]} data-purpose="instructor-name-top">{t5}{t8}</div>;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp3(acc, curr, index) {
  if (index === 0) {
    return [curr];
  }
  return [...acc, ", ", curr];
}
function _temp2(instructor_0) {
  return <Button componentClass="a" udStyle="link" key={instructor_0.id} className={classNames("ud-text-sm", styles["instructor-link"])} href={instructor_0.url} target="_blank" rel="noopener noreferrer">{instructor_0.name}</Button>;
}
function _temp(event) {
  const target = event.target as HTMLElement;
  const parentElement = target.parentElement as HTMLAnchorElement | null;
  const position = parentElement?.dataset?.position || "1";
  const id = generateInstructorAnchorHash(parseInt(position, 10) - 1);
  if (parentElement) {
    parentElement.href = `${window.location.search}${id}`;
  }
}